import * as React from "react";
import DOMPurify from "dompurify";
import { Block } from "kanopi-components/Block";
import { LabelXSmall, ParagraphXSmall } from "kanopi-components/typography";
import { LinkList } from "kanopi-components/LinkList";
import { Modal } from "kanopi-components/Modal";
import { parseAndPurifyHtml } from "kw/util/utils";
import { useStyletron } from "kanopi-components/theme/useStyletron";
import { useUser } from "kw/auth/useUser";
import { DisclaimerLinks } from "./DisclaimerLinks";

export const Footer: React.FC<{ inline?: boolean }> = ({ inline = true }) => {
  const user = useUser();
  const [disclaimer, setDisclaimer] = React.useState("");
  const [disclaimerLinks, setDisclaimerLinks] = React.useState([]);
  // Add a hook to make all links open a new window
  DOMPurify.addHook("afterSanitizeAttributes", function (node) {
    // set all elements owning target to target=_blank
    if ("target" in node) {
      node.setAttribute("target", "_blank");
      node.setAttribute("rel", "noopener");
    }
  });
  React.useEffect(() => {
    setDisclaimer(user?.organization?.disclaimer);
    setDisclaimerLinks(user?.organization?.disclaimerLinks);
  }, [user]);

  const renderProps = {
    disclaimer,
    disclaimerLinks,
  };

  return inline ? (
    <DisclaimerInline {...renderProps} />
  ) : (
    <DisclaimerModal {...renderProps} />
  );
};

const DisclaimerModal: React.FC<{
  disclaimer: string;
  disclaimerLinks: { name: string; url: string }[];
}> = ({ disclaimer, disclaimerLinks }) => {
  const [, theme] = useStyletron();
  const [open, setOpen] = React.useState(false);
  return (
    <Block display="flex" justifyContent="right">
      <LabelXSmall
        onClick={() => setOpen(true)}
        color={theme.colors.contentSecondary}
        overrides={{
          Block: {
            style: {
              cursor: "pointer",
            },
          },
        }}
      >
        Disclaimer
      </LabelXSmall>
      <Modal isOpen={open} onClose={() => setOpen(false)} animate autoFocus>
        <Modal.Body>
          <Block
            marginTop={theme.sizing.scale1200}
            marginRight={theme.sizing.scale800}
            marginLeft={theme.sizing.scale800}
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
          >
            {disclaimerLinks ? (
              <LinkList
                items={disclaimerLinks.map((link) => ({
                  href: link.url,
                  label: link.name,
                }))}
              />
            ) : null}
            <ParagraphXSmall
              color={theme.colors.mono700}
              $style={{ alignSelf: "center" }}
            >
              {parseAndPurifyHtml(disclaimer)}
            </ParagraphXSmall>
          </Block>
        </Modal.Body>
      </Modal>
    </Block>
  );
};

const DisclaimerInline: React.FC<{
  disclaimer: string;
  disclaimerLinks: { name: string; url: string }[];
}> = ({ disclaimer, disclaimerLinks }) => {
  const [, theme] = useStyletron();
  return (
    <>
      {disclaimer && (
        <Block
          marginTop={theme.sizing.scale1200}
          marginRight={theme.sizing.scale800}
          marginLeft={theme.sizing.scale800}
          marginBottom={theme.sizing.scale1200}
          width={"100%"}
          display="flex"
          flexDirection="column"
          alignItems="center"
          overrides={{
            Block: {
              style: ({ $theme }) => ({
                borderTop: `1px solid ${$theme.colors.backgroundTertiary}`,
              }),
            },
          }}
        >
          <ParagraphXSmall color={theme.colors.mono700}>
            {parseAndPurifyHtml(disclaimer)}
          </ParagraphXSmall>
          {disclaimerLinks ? <DisclaimerLinks links={disclaimerLinks} /> : null}
        </Block>
      )}
    </>
  );
};
